import { Icon, MuiAlert, Tooltip, Typography } from '@fox/atoms';
import { DeviceType, FlexColumnSpaced, FlexRowCenterJustify, useDeviceType } from '@fox/layout';
import { Rating } from '@mui/material';
import { useState } from 'react';
import { useContentContext } from '../../content-context';
import { useUpsertRating } from '../mutation-hooks/mutation-implementations';
import { useAverageRatingForContent, useGetMemberRatingForContent } from '../query-hooks/query-implementations';
import { RatingProps } from './contracts';

function getStarFontSize(starSize: 'small' | 'medium' | 'large' | undefined, deviceType: DeviceType): string {
	switch (starSize) {
		case 'small':
			return '1rem';
		case 'medium':
			return '2rem';
		case 'large':
			return deviceType === DeviceType.mobile ? '3rem' : '4rem';
		default:
			return '2rem';
	}
}
export function Ratings({ starSelectedColour, starUnselectedColour, starSize }: RatingProps) {
	const { contentId, requestToken, isLoggedIn } = useContentContext();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const deviceType = useDeviceType();

	const { mutate, error: upsertError } = useUpsertRating({ requestToken });
	const {
		data: memberRating,
		loading: memberLoading,
		error: memberError,
		refetch: refetchMemberRating
	} = useGetMemberRatingForContent({ requestToken, contentId });
	const {
		data: averageRating,
		loading: averageLoading,
		error: averageError,
		refetch: refetchAverageRating
	} = useAverageRatingForContent({ requestToken, contentId });

	const starSizeRem = getStarFontSize(starSize, deviceType);

	const handleRatingChange = async (_: React.SyntheticEvent, newValue: number | null) => {
		if (newValue === null || isSubmitting) return;

		setIsSubmitting(true);

		try {
			await mutate({
				contentId,
				score: newValue
			});

			// Refetch after successful submission
			await Promise.all([refetchMemberRating(), refetchAverageRating()]);
		} catch (error) {
			console.error('Rating submission error:', error);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Show error if initial data fetch fails
	if (memberError || averageError) {
		return (
			<FlexRowCenterJustify>
				<MuiAlert severity="error">{memberError || averageError}</MuiAlert>
			</FlexRowCenterJustify>
		);
	}

	// Helper function to round to nearest 0.5
	const roundToHalf = (value: number): number => {
		return Math.round(value * 2) / 2;
	};

	// Determine rating value and text based on login state
	const getRatingValue = () => {
		if (isLoggedIn) {
			return memberRating || 0;
		} else {
			// Not logged in - show average rating or 0 if no ratings
			return averageRating?.averageScore ? roundToHalf(averageRating.averageScore) : 0;
		}
	};

	const getRatingText = () => {
		if (isLoggedIn) {
			if (memberRating && memberRating > 0) {
				return `You rated ${memberRating} star${memberRating !== 1 ? 's' : ''}`;
			} else {
				return 'Leave a rating';
			}
		} else {
			// Not logged in
			if (averageRating && averageRating.totalCount > 0) {
				return `${averageRating.averageScore?.toFixed(1)} (${averageRating.totalCount} rating${
					averageRating.totalCount !== 1 ? 's' : ''
				})`;
			} else {
				return 'No ratings';
			}
		}
	};

	return (
		<FlexColumnSpaced>
			<FlexRowCenterJustify>
				<Tooltip placement="top" title={!isLoggedIn ? 'Please log in to submit rating.' : ''} disableHoverListener={isLoggedIn}>
					<div>
						<Rating
							value={getRatingValue()}
							precision={isLoggedIn ? 1 : 0.5}
							icon={<Icon iconName="Star" sx={{ color: starSelectedColour, fontSize: starSizeRem }} />}
							emptyIcon={<Icon iconName="StarBorder" sx={{ color: starUnselectedColour, fontSize: starSizeRem }} />}
							max={5}
							onChange={isLoggedIn ? handleRatingChange : undefined}
							disabled={isSubmitting || memberLoading || averageLoading}
							readOnly={!isLoggedIn}
						/>
					</div>
				</Tooltip>
			</FlexRowCenterJustify>

			{/* Show rating text */}
			<FlexRowCenterJustify>
				<Typography variant="body2" sx={{ color: 'text.secondary' }}>
					{getRatingText()}
				</Typography>
			</FlexRowCenterJustify>

			{/* Show average rating for logged-in users who have rated */}
			{isLoggedIn && memberRating && memberRating > 0 && averageRating && averageRating.totalCount > 0 && (
				<FlexRowCenterJustify>
					<Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
						Average: {averageRating.averageScore?.toFixed(1)} ({averageRating.totalCount} rating
						{averageRating.totalCount !== 1 ? 's' : ''})
					</Typography>
				</FlexRowCenterJustify>
			)}

			{/* Show general upsert error */}
			{upsertError && (
				<FlexRowCenterJustify>
					<MuiAlert severity="error">{upsertError}</MuiAlert>
				</FlexRowCenterJustify>
			)}
		</FlexColumnSpaced>
	);
}
